import {
  Button,
  ButtonProps,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  DialogTitleProps
} from '@mui/material'
import React from 'react'
import { useBoolean } from 'react-use'

type SetModalState = (nextState?: boolean) => void

export type ButtonWithConfirmationOnConfirm = (options: {
  setIsModalOpen: SetModalState
}) => unknown

export const ButtonWithConfirmation: React.FC<
  Omit<ButtonProps, 'onClick'> & {
    buttonCancelProps?: Omit<ButtonProps, 'onClick'>
    buttonCancelText?: string
    buttonConfirmProps?: Omit<ButtonProps, 'onClick'>
    buttonConfirmText: string
    dialogBodyContent: string | React.ReactElement
    dialogTitleColor?: DialogTitleProps['color']
    dialogTitleContent: string | React.ReactElement
    onConfirm: ButtonWithConfirmationOnConfirm
    renderButton?: (props: {
      setIsModalOpen: SetModalState
    }) => React.ReactElement
  }
> = ({
  buttonCancelProps,
  buttonCancelText = 'Cancel',
  buttonConfirmProps,
  buttonConfirmText,
  children,
  dialogBodyContent,
  dialogTitleContent,
  dialogTitleColor,
  onConfirm,
  renderButton,
  ...rest
}) => {
  const [isModalOpen, setIsModalOpen] = useBoolean(false)

  const handleConfirmClick = async () => {
    await onConfirm({ setIsModalOpen })
  }

  return (
    <>
      {renderButton ? (
        renderButton({ setIsModalOpen })
      ) : (
        <Button
          variant="contained"
          {...rest}
          onClick={() => setIsModalOpen(true)}
        >
          {children}
        </Button>
      )}
      <Dialog
        aria-labelledby="alert-dialog-title"
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      >
        <DialogTitle color={dialogTitleColor} id="alert-dialog-title">
          {dialogTitleContent}
        </DialogTitle>
        <DialogContent>
          {typeof dialogBodyContent === 'string' ? (
            <DialogContentText>{dialogBodyContent}</DialogContentText>
          ) : (
            dialogBodyContent
          )}
        </DialogContent>
        <DialogActions>
          <Button
            data-testid={`dialog-button-cancel-${buttonCancelText}`}
            {...buttonCancelProps}
            onClick={() => setIsModalOpen(false)}
          >
            {buttonCancelText}
          </Button>
          <Button
            autoFocus
            data-testid={`dialog-button-confirm-${buttonConfirmText}`}
            variant="contained"
            onClick={handleConfirmClick}
            {...buttonConfirmProps}
          >
            {buttonConfirmText}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
